import { z } from 'zod';
import type { Message } from '@/components/chat/chat-types';

export interface ToolDigestDebugEntry {
  assistantMessageId: string;
  cursor: string | null;
  dataset: string | null;
  digest: Record<string, unknown>;
  itemCount: number | null;
  limits?: Record<string, unknown> | null;
  toolCallId: string;
}

const ToolResultSchema = z
  .object({
    digest: z.record(z.string(), z.unknown()),
    dataset: z.string().nullable().optional(),
    items: z.array(z.unknown()).optional(),
    totalItems: z.number().optional(),
    pagination: z
      .object({
        cursor: z.string().nullable().optional(),
      })
      .optional(),
    limits: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .passthrough();

export function extractToolResultDigests(messages: Message[] | undefined | null): ToolDigestDebugEntry[] {
  if (!Array.isArray(messages) || messages.length === 0) {
    return [];
  }

  const entries: ToolDigestDebugEntry[] = [];

  for (const message of messages) {
    if (!message) {
      continue;
    }
    if (!Array.isArray(message.toolInvocations)) {
      continue;
    }

    for (const invocation of message.toolInvocations) {
      if (!invocation) {
        continue;
      }
      const result = invocation.result;
      const parsedResult = ToolResultSchema.safeParse(result);
      if (!parsedResult.success) {
        continue;
      }
      const { digest, dataset: datasetRaw, items, totalItems, pagination, limits } = parsedResult.data;

      let itemCount: number | null = null;
      if (Array.isArray(items)) {
        itemCount = items.length;
      } else {
        itemCount = typeof totalItems === 'number' ? totalItems : null;
      }
      const cursor = pagination?.cursor ?? null;
      const dataset = datasetRaw ?? null;

      entries.push({
        assistantMessageId: message.id,
        toolCallId: invocation.toolCallId,
        dataset,
        itemCount,
        cursor,
        digest,
        limits: limits ?? null,
      });
    }
  }

  return entries;
}
